<script lang="ts">
	/**
	 * `/SalesFunnel` — how the store is doing (Design §12).
	 *
	 * **Not part of Operations**, and the split is by question rather than by tidiness. Operations
	 * answers "is the machinery running" and is read during an incident; this answers "how is the
	 * store doing" and is read on a Monday morning. Folding them puts a heartbeat table between a
	 * funnel and a revenue chart, and the page stops being about either question.
	 *
	 * **One filter bar drives every panel**, and it is reflected into the URL so a slice can be sent
	 * to somebody. Each panel then fetches its own data and renders its own loading, empty and error
	 * state — one slow endpoint costs that panel rather than the screen, exactly as the Operations
	 * page already does it. An operator opening a dashboard during a bad week is the worst possible
	 * moment to hand back a blank page because one query timed out.
	 *
	 * Panels are in the order the questions get asked: how did we do, where are we losing people,
	 * what is the shape over time, where did they come from, which products sell, who are they, who
	 * gave up, and finally one real visit at a time.
	 */

	import { onMount } from 'svelte';
	import { PageHeader } from '../../components';
	import type { AnalyticsFilterOptions, AnalyticsOverview, AnalyticsQuery } from '../../AppTypes';
	import { getAnalyticsFilters, getAnalyticsOverview } from '../../AppNetwork';
	import FilterBar from './analytics/FilterBar.svelte';
	import HeadlinePanel from './analytics/HeadlinePanel.svelte';
	import TrendPanel from './analytics/TrendPanel.svelte';
	import BreakdownPanel from './analytics/BreakdownPanel.svelte';
	import ProductsPanel from './analytics/ProductsPanel.svelte';
	import AbandonmentPanel from './analytics/AbandonmentPanel.svelte';
	import SessionsPanel from './analytics/SessionsPanel.svelte';
	import SessionTimeline from './analytics/SessionTimeline.svelte';

	/** Thirty days, which is the window an operator means when they do not say. */
	function fnDefaultFilter(): AnalyticsQuery {
		const to = new Date();
		return {
			from: new Date(to.getTime() - 30 * 86400000).toISOString(),
			to: to.toISOString(),
			granularity: 'day'
		};
	}

	let filter: AnalyticsQuery = fnDefaultFilter();
	let options: AnalyticsFilterOptions | null = null;
	let overview: AnalyticsOverview | null = null;
	let overviewError = '';
	let overviewLoading = false;
	let openSession = '';

	/**
	 * Read the filter out of the URL on arrival.
	 *
	 * `history.replaceState` rather than the router's `push`, because changing a filter is not a
	 * navigation: pushing would fill the back button with twelve copies of the same page and make
	 * "back" mean "undo one filter change", which is not what anybody presses it for.
	 */
	onMount(() => {
		const parameters = new URLSearchParams(window.location.search);
		const fromURL: AnalyticsQuery = { ...fnDefaultFilter() };
		for (const [key, value] of parameters.entries()) {
			if (value.length === 0) continue;
			if (key === 'includeBots') {
				fromURL.includeBots = value === 'true';
			} else {
				(fromURL as Record<string, unknown>)[key] = value;
			}
		}
		filter = fromURL;
		void fnLoadOptions();
	});

	async function fnLoadOptions(): Promise<void> {
		try {
			options = await getAnalyticsFilters();
		} catch {
			// The bar degrades to its date presets and its switch. A filter list that could not be
			// fetched must not cost the page every panel behind it.
			options = null;
		}
	}

	$: overviewKey = JSON.stringify(filter);
	$: void fnLoadOverview(overviewKey);

	async function fnLoadOverview(_key: string): Promise<void> {
		overviewLoading = true;
		try {
			overview = await getAnalyticsOverview(filter);
			overviewError = '';
		} catch (caught) {
			overviewError = caught instanceof Error ? caught.message : 'Failed to load the overview';
			overview = null;
		} finally {
			overviewLoading = false;
		}
	}

	function fnFilterChanged(event: CustomEvent<AnalyticsQuery>): void {
		filter = event.detail;
		const parameters = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value === undefined || value === null || value === '' || value === false) continue;
			parameters.set(key, String(value));
		}
		window.history.replaceState({}, '', `${window.location.pathname}?${parameters.toString()}`);
	}
</script>

<PageHeader title="Sales Funnel">
	<svelte:fragment slot="description">
		Who looked, what they looked at, and where they stopped — for signed-in customers and
		anonymous visitors alike.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<p class="mb-2">
			Every money figure here comes from the store's own orders and invoices, joined to the visit
			that produced them. Nothing on this page is a client-reported sale, so revenue here and
			revenue on Operations are the same number sliced two ways.
		</p>
		<p class="mb-2">
			Crawlers are excluded by default and the count held back is stated beside the switch —
			counting them puts a storefront's conversion rate at about a third of a per cent.
		</p>
		<p>
			The filter is in the address bar, so a slice can be sent to somebody. Abandonment is three
			separate numbers because the three have three different fixes.
		</p>
	</svelte:fragment>
</PageHeader>

<div class="space-y-6 min-w-0">
	<FilterBar
		{filter}
		{options}
		botSessionsExcluded={overview?.botSessionsExcluded ?? 0}
		busy={overviewLoading}
		on:change={fnFilterChanged}
	/>

	<HeadlinePanel {overview} loading={overviewLoading} error={overviewError} />

	<TrendPanel {filter} />

	<BreakdownPanel
		{filter}
		title="Acquisition"
		faIcon="signs-post"
		help="Where visits came from, with what they bought beside them — a channel that sends traffic and one that sends buyers look identical until the money is on the same row. Links tagged with utm_source, utm_medium and utm_campaign are attributed to those; untagged visits fall back to the referring site, else Direct."
		dimensions={[
			{ value: 'source', name: 'Source', description: 'Where the visit came from: utm_source, else the referring site.' },
			{ value: 'medium', name: 'Medium', description: 'The kind of traffic, from utm_medium: email, cpc, social and so on.' },
			{ value: 'campaign', name: 'Campaign', description: 'The campaign named in utm_campaign.' },
			{ value: 'referrer', name: 'Referring site', description: 'The site the visitor followed a link from.' },
			{ value: 'landing', name: 'Landing page', description: 'The first storefront page of the visit.' }
		]}
	/>

	<ProductsPanel {filter} />

	<BreakdownPanel
		{filter}
		title="Audience"
		faIcon="users-viewfinder"
		dimensions={[
			{ value: 'country', name: 'Country', description: 'From the CDN’s country header, or the customer’s billing country once known.' },
			{ value: 'device', name: 'Device', description: 'Desktop, mobile or tablet, from the browser’s user agent.' },
			{ value: 'browser', name: 'Browser', description: 'The browser family, from the user agent.' },
			{ value: 'os', name: 'Operating system', description: 'The operating system, from the user agent.' },
			{ value: 'language', name: 'Language', description: 'The browser’s preferred language.' }
		]}
	/>

	<BreakdownPanel
		{filter}
		title="Pages and events"
		faIcon="file-lines"
		help="What was viewed and what happened on it. These count events rather than visits, so they carry no purchases column — a page view does not convert."
		dimensions={[
			{ value: 'path', name: 'Page', description: 'Each storefront page, by address.' },
			{ value: 'type', name: 'Event', description: 'What happened: page views, product views, checkout steps and so on.' },
			{ value: 'product', name: 'Product', description: 'The product an event was about. Events about no product are left out.' },
			{ value: 'family', name: 'Product family', description: 'The product line an event was about.' },
			{ value: 'currency', name: 'Currency shown', description: 'The currency prices were displayed in.' }
		]}
	/>

	<AbandonmentPanel {filter} on:open={event => (openSession = event.detail)} />

	<SessionsPanel {filter} on:open={event => (openSession = event.detail)} />
</div>

<SessionTimeline
	sessionUUID={openSession}
	on:close={() => (openSession = '')}
	on:open={event => (openSession = event.detail)}
/>
